import React, { useState } from 'react';
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
} from '@mui/material';
import { AppContext } from '../../../App';
import SelectTrainingSetWithSearch from '../../extractionBatches/create/SelectTrainingSetWithSearch';
import { ITrainingSet } from '../../../types/my-types';
import { useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import { useDocumentUpdate } from '../../hooks/useDocumentUpdate';
import ButtonGlynt from '../../common/Buttons/ButtonGlynt';

interface ISetTargetTrainingSetProps {
  documentId: string;
}

function SetTargetTrainingSet({ documentId }: ISetTargetTrainingSetProps) {
  const { setToast, dataPoolId } = React.useContext(AppContext);

  const [open, setOpen] = React.useState(false);
  const [trainingSet, setTrainingSet] = useState<ITrainingSet>();

  const queryClient = useQueryClient();

  const { mutateAsync: updateDocument, isPending: isLoadingMutation } = useDocumentUpdate({
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['trainingSets']
      });
      queryClient.invalidateQueries({
        queryKey: ['documents']
      });
      setToast({
        variant: 'success',
        open: true,
        message: 'Documents added successfully.',
      });
      setOpen(false);
    },
    onError: (error: AxiosError) => {
      let message = error.message || 'Sorry, failed to add to training set.';
      setToast({
        variant: 'error',
        open: true,
        message,
      });
    },
  });

  const closeModal = () => {
    setOpen(false);
  };

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
        <ButtonGlynt
          variant="outlined"
          size="xs"
          data-cy="btn-set-target-training-set"
          onClick={() => {
            setOpen(true);
          }}
          sx={{ marginTop: '5px' }}
        >
          Assign
        </ButtonGlynt>
      </div>

      <Dialog
        fullWidth
        maxWidth="sm"
        open={open}
        style={{ display: `${open ? '' : 'none'}` }}
        onClose={(e, reason) => {
          if (reason !== 'backdropClick') {
            closeModal();
          }
        }}
        aria-labelledby="form-dialog-title"
      >
        <DialogTitle id="form-dialog-title">{`Set Targeted Training Set`}</DialogTitle>
        <DialogContent>
          <Grid container spacing={1}>
            <Grid container justifyContent="center" alignItems="center" item xs={12}>
              <SelectTrainingSetWithSearch
                setTrainingSet={setTrainingSet}
                disabled={false}
                width="100%"
                trainedOnly={false}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            id="training-set-add-dialog-cancel"
            data-cy="training-set-add-dialog-cancel"
            onClick={() => {
              closeModal();
            }}
            color="primary"
          >
            Cancel
          </Button>

          <Button
            id="button-add-to-training-set-submit"
            color="primary"
            data-cy="button-add-to-training-set-submit"
            onClick={async () => {
              if (!trainingSet) {
                setToast({
                  open: true,
                  variant: 'error',
                  message: 'Unable to get training set data.',
                });
                return;
              }

              await updateDocument({
                id: documentId,
                data: {
                  target_training_set: trainingSet.id,
                },
              });
            }}
            disabled={isLoadingMutation || !trainingSet}
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default SetTargetTrainingSet;
